<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useApiClient } from "@/api/client";
import ColorPaletteDO = App.Data.ColorPaletteDO;

const props = defineProps<{
	initialColor?: string;
}>();

const emit = defineEmits<{
	apply: [color: string, paletteUuid: string];
	stageColor: [color: string, paletteUuid: string];
}>();

const rgbNames = ["Red", "Green", "Blue"];
const rgbValuesRaw = ref<number[]>([0, 0, 0]);
const hexRegex = /^#?[0-9A-F]{6}$/i;
const hexValueRaw = ref("");

const rgbValues = ref([0, 1, 2].map((i) => computedRgbValueFactory(i)));

const isEyeDropperSupported = computed(() => "EyeDropper" in window);

const combinedColors = computed(() =>
	[0, 1, 2].reduce(
		(acc, i) =>
			acc +
			(rgbValues.value[i].value
				?.toString(16)
				?.toUpperCase()
				?.padStart(2, "0") ?? "00"),
		"#"
	)
);

const hexInput = computed({
	get: () => combinedColors.value,
	set: (val: string) => {
		hexValueRaw.value = val;
		if (hexRegex.test(val)) {
			setRgbValues(val, val.length - 6);
		}
	},
});

function computedRgbValueFactory(i: number) {
	return computed({
		get: () => rgbValuesRaw.value[i],
		set: (val: number) => {
			rgbValuesRaw.value[i] = isNaN(val) ? 0 : Math.min(255, Math.max(0, val));
		},
	});
}

function setRgbValues(hexString: string, valuesStart: number = 1) {
	[0, 1, 2].forEach((i) => {
		const stringIndex = valuesStart + i * 2;
		rgbValues.value[i].value = parseInt(
			hexString.substring(stringIndex, stringIndex + 2),
			16
		);
	});
}

const onPresetClick = (color: string) => {
	setRgbValues(color);
	emit("stageColor", color, selectedPalette.value?.uuid ?? "");
};

const onApply = () => {
	emit("apply", combinedColors.value, selectedPalette.value?.uuid ?? "");
};

async function enableEyeDropper() {
	if (!isEyeDropperSupported.value) return;
	try {
		// @ts-ignore
		const eyeDropper = new EyeDropper();
		const result = await eyeDropper.open();
		setRgbValues(result.sRGBHex);
	} catch {
		// user cancelled
	}
}

const palettes = ref<ColorPaletteDO[]>([]);
const selectedPalette = ref<ColorPaletteDO | null>(null);

const paletteColors = computed<(string | null)[]>(() => {
	if (!selectedPalette.value) return [null];
	return (
		selectedPalette.value.colors?.map((c) => c.hex_value ?? null) ?? [null]
	);
});

const selectPalette = (palette: ColorPaletteDO) => {
	selectedPalette.value = palette;
};

onMounted(async () => {
	if (props.initialColor) setRgbValues(props.initialColor);
	const response = await useApiClient("profile").getColorPalettes();
	if (response.isOk()) {
		palettes.value = response.value;
		selectedPalette.value =
			response.value.find((p) => p.is_default) ?? response.value[0] ?? null;
	}
});

watch(
	() => props.initialColor,
	(val) => {
		if (val) setRgbValues(val);
	}
);
</script>

<template>
	<v-select
		:model-value="selectedPalette"
		:items="palettes"
		item-title="name"
		item-value="uuid"
		return-object
		density="compact"
		variant="outlined"
		hide-details
		class="mb-2"
		@update:model-value="selectPalette"
	/>

	<div class="color-grid">
		<div
			v-for="(paletteColor, i) in paletteColors"
			:key="i"
			:style="paletteColor ? { backgroundColor: paletteColor } : {}"
			:class="[
				'color-preview--small',
				{ 'color-preview--empty': !paletteColor },
			]"
			@click="paletteColor ? onPresetClick(paletteColor) : undefined"
		/>
	</div>

	<div class="mt-1 mb-2 d-flex flex-row align-center">
		<v-divider thickness="2" />
		<span class="mx-2 text-subtitle-2">OR</span>
		<v-divider thickness="2" />
	</div>

	<div v-for="(colorName, i) in rgbNames" :key="i" class="selection-bar">
		<span class="ml-2 text-subtitle-1 selection-label">{{ colorName }}</span>
		<v-slider
			v-model="rgbValues[i].value"
			density="compact"
			class="mt-n2 mb-n3"
			:min="0"
			:max="255"
			:step="1"
			thumb-size="13"
		>
			<template #append>
				<v-text-field
					v-model.number="rgbValues[i].value"
					density="compact"
					bg-color="surface"
					class="ml-2 mt-n2"
					style="width: 55px"
					type="number"
					variant="underlined"
					hide-details
				/>
			</template>
		</v-slider>
	</div>

	<div class="bottom-row">
		<v-btn
			v-if="isEyeDropperSupported"
			icon="mdi-eyedropper"
			color="#888888"
			variant="plain"
			height="28"
			width="28"
			@click="enableEyeDropper"
		/>
		<v-btn
			variant="flat"
			color="secondary"
			rounded="0"
			height="28"
			border="sm"
			@click="onApply"
		>
			apply
		</v-btn>
		<v-sheet color="secondary" border="sm" height="28" width="100">
			<v-text-field
				v-model="hexInput"
				density="compact"
				class="hex-input"
				variant="plain"
				bg-color="transparent"
				hide-details
			/>
		</v-sheet>
		<v-sheet :color="combinedColors" border="sm" height="28" width="28" />
	</div>
</template>

<style scoped lang="scss">
.color-grid {
	display: grid;
	grid-template-columns: repeat(20, 1fr);
	gap: 2px;
}

.color-preview--small {
	cursor: pointer;
	height: 1.25rem;
	width: 1.25rem;

	&.color-preview--empty {
		background-color: #eeeeee;
		border: 1px solid #d5d5d5;
		cursor: not-allowed;
	}
}

.selection-bar {
	display: grid;
	gap: 0.5rem;
	grid-template-columns: auto 1fr;
}

.selection-label {
	width: 52px;
	margin-top: -5px;
	color: #888888;
}

.bottom-row {
	display: flex;
	flex-direction: row;
	justify-content: center;
	gap: 4px;
}

.hex-input {
	font-family: "Courier New", monospace;
	max-width: 70px;
	display: block;
	margin-top: -7px;
	margin-left: auto;
	margin-right: auto;
}
</style>
